import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Search, Package, Download, Eye, Edit, Warehouse } from 'lucide-react';
import useProtectedExport from '../hooks/useProtectedExport';
import ExportPasswordDialog from '../components/ExportPasswordDialog';

const InventoryList = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';
  const [inventory, setInventory] = useState([]);
  const [summary, setSummary] = useState({});
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const protectedExport = useProtectedExport();
  const [searchTerm, setSearchTerm] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [selectedProductGroup, setSelectedProductGroup] = useState(null);
  const [adjustData, setAdjustData] = useState({ qty_adjustment: 0, reason: '' });

  // Filter states
  const [filterBrand, setFilterBrand] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterModel, setFilterModel] = useState('');

  const fetchInventory = useCallback(async () => {
    try {
      let url = '/inventory/';
      const params = new URLSearchParams();
      if (filterBrand) params.append('brand_id', filterBrand);
      if (filterCategory) params.append('category_id', filterCategory);
      if (filterSupplier) params.append('supplier_id', filterSupplier);
      if (searchTerm) params.append('search', searchTerm);
      if (params.toString()) url += `?${params.toString()}`;

      const [invRes, summaryRes] = await Promise.all([
        api.get(url),
        api.get('/inventory/summary')
      ]);
      setInventory(invRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Failed to fetch inventory');
    } finally {
      setLoading(false);
    }
  }, [filterBrand, filterCategory, filterSupplier, searchTerm]);

  const fetchMasterData = useCallback(async () => {
    try {
      const [brandsRes, categoriesRes, suppliersRes, productsRes] = await Promise.all([
        api.get('/brands/'),
        api.get('/categories/'),
        api.get('/suppliers/'),
        api.get('/products/')
      ]);
      setBrands(brandsRes.data);
      setCategories(categoriesRes.data);
      setSuppliers(suppliersRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Failed to fetch master data');
    }
  }, []);

  useEffect(() => {
    fetchInventory();
    fetchMasterData();
  }, [fetchInventory, fetchMasterData]);

  // Get unique models from inventory
  const uniqueModels = [...new Set(inventory.map(item => item.product_model).filter(Boolean))];

  // Filter available stock only and group by product
  const availableInventory = inventory.filter(inv => inv.qty_in_stock > 0);
  
  const filteredInventory = availableInventory.filter(item => {
    const matchesSearch = !searchTerm || 
      (item.product_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.product_sku || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.product_model || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesBrand = !filterBrand || item.brand_id === filterBrand;
    const matchesCategory = !filterCategory || item.category_id === filterCategory;
    const matchesSupplier = !filterSupplier || item.supplier_id === filterSupplier;
    const matchesModel = !filterModel || item.product_model === filterModel;
    
    return matchesSearch && matchesBrand && matchesCategory && matchesSupplier && matchesModel;
  });

  // Group by product for summary view
  const productSummary = filteredInventory.reduce((acc, item) => {
    const productId = item.product_id;
    if (!acc[productId]) {
      acc[productId] = {
        product_id: productId,
        product_id_code: item.product_id_code,
        product_name: item.product_name,
        product_sku: item.product_sku,
        product_model: item.product_model,
        brand_name: item.brand_name,
        brand_id: item.brand_id,
        category_name: item.category_name,
        category_id: item.category_id,
        selling_price: item.selling_price,
        purchase_price: item.purchase_price,
        unit: item.unit,
        reorder_level: item.reorder_level || 5,
        totalQty: 0,
        totalValue: 0,
        items: []
      };
    }
    acc[productId].totalQty += item.qty_in_stock || 0;
    acc[productId].totalValue += item.stock_value || 0;
    acc[productId].items.push(item);
    return acc;
  }, {});

  const productList = Object.values(productSummary);

  const handleViewProductGroup = (group) => {
    setSelectedProductGroup(group);
    setViewDialogOpen(true);
  };

  const handleViewProduct = async (inv) => {
    try {
      const response = await api.get(`/inventory/product/${inv.product_id}`);
      setSelectedInventory(response.data);
      setSelectedProductGroup(null);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load product inventory');
    }
  };

  const handleOpenAdjust = (inv) => {
    setSelectedInventory(inv);
    setAdjustData({ qty_adjustment: 0, reason: '' });
    setAdjustDialogOpen(true);
  };

  const handleAdjust = async () => {
    if (!adjustData.reason) {
      toast.error('Please provide a reason for adjustment');
      return;
    }

    try {
      await api.put(`/inventory/${selectedInventory.id}/adjust`, null, {
        params: adjustData
      });
      toast.success('Inventory adjusted successfully');
      setAdjustDialogOpen(false);
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust inventory');
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filterBrand) params.append('brand_id', filterBrand);
      if (filterCategory) params.append('category_id', filterCategory);
      if (filterSupplier) params.append('supplier_id', filterSupplier);

      const response = await api.get(`/inventory/export?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inventory_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Inventory exported successfully');
    } catch (error) {
      toast.error('Failed to export inventory');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
          <p className="text-gray-600">View and manage stock levels</p>
        </div>
        <Button onClick={() => protectedExport.requestExport(handleExport)} variant="outline" style={{display: isAdmin ? undefined : 'none'}}>
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Items</p>
                <p className="text-2xl font-bold">{summary.total_items || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Warehouse className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Quantity</p>
                <p className="text-2xl font-bold">{summary.total_qty?.toLocaleString('en-IN') || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Package className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Unique Products</p>
                <p className="text-2xl font-bold">{summary.unique_products || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Warehouse className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Stock Value</p>
                <p className="text-2xl font-bold">₹{summary.total_value?.toLocaleString('en-IN') || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterBrand || 'all'} onValueChange={(v) => setFilterBrand(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="All Brands" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Brands</SelectItem>
                {brands.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCategory || 'all'} onValueChange={(v) => setFilterCategory(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterModel || 'all'} onValueChange={(v) => setFilterModel(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="All Models" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Models</SelectItem>
                {uniqueModels.map(m => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => {
              setSearchTerm('');
              setFilterBrand('');
              setFilterCategory('');
              setFilterSupplier('');
              setFilterModel('');
            }}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Inventory Table - Grouped by Product */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Warehouse className="h-5 w-5" />
            Available Stock ({productList.length} products, {filteredInventory.reduce((sum, i) => sum + i.qty_in_stock, 0)} units)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Brand</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Total Qty</TableHead>
                <TableHead className="text-right">Selling Price</TableHead>
                <TableHead className="text-right">Stock Value</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {productList.length > 0 ? (
                productList.map((group) => {
                  const isLowStock = group.totalQty <= group.reorder_level;
                  return (
                    <TableRow key={group.product_id} className={isLowStock ? 'bg-orange-50' : ''}>
                      <TableCell>
                        <div>
                          <span className="font-medium">{group.product_name}</span>
                          <p className="text-xs text-gray-500">SKU: {group.product_sku || 'N/A'}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {group.product_model ? (
                          <Badge variant="outline" className="bg-gray-50">{group.product_model}</Badge>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700">{group.brand_name || '-'}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-purple-50 text-purple-700">{group.category_name || '-'}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        <span className={isLowStock ? 'text-orange-600' : 'text-emerald-600 text-lg'}>
                          {group.totalQty}
                        </span>
                        <span className="text-gray-500 ml-1 text-sm">{group.unit}</span>
                        {isLowStock && (
                          <Badge className="ml-2 bg-orange-100 text-orange-700 text-xs">Low</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">₹{group.selling_price?.toLocaleString('en-IN') || '0'}</TableCell>
                      <TableCell className="text-right font-medium text-green-700">₹{group.totalValue?.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => handleViewProductGroup(group)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-gray-500 py-8">
                    No available stock items found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* View Product Inventory Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="stock-view-dialog">
          <DialogHeader>
            <DialogTitle>Product Inventory Details</DialogTitle>
          </DialogHeader>
          {selectedProductGroup ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Product</Label>
                  <p className="font-medium">
                    {selectedProductGroup.product_name}
                    {selectedProductGroup.product_model && ` (${selectedProductGroup.product_model})`}
                  </p>
                  <p className="text-sm text-gray-500">SKU: {selectedProductGroup.product_sku || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Brand / Category</Label>
                  <div className="flex gap-2 mt-1">
                    <Badge variant="outline" className="bg-blue-50 text-blue-700">{selectedProductGroup.brand_name || '-'}</Badge>
                    <Badge variant="outline" className="bg-purple-50 text-purple-700">{selectedProductGroup.category_name || '-'}</Badge>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
                <div>
                  <Label className="text-gray-500">Total Quantity</Label>
                  <p className="text-2xl font-bold text-emerald-600">{selectedProductGroup.totalQty}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Total Value</Label>
                  <p className="text-2xl font-bold text-green-700">₹{selectedProductGroup.totalValue?.toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Selling Price</Label>
                  <p className="text-2xl font-bold">₹{selectedProductGroup.selling_price?.toLocaleString('en-IN') || '0'}</p>
                </div>
              </div>

              {/* Stock entries breakdown (if multiple entries) */}
              {selectedProductGroup.items && selectedProductGroup.items.length > 0 && (
                <div className="border rounded-lg">
                  <p className="p-3 bg-gray-50 font-medium border-b">Stock Entries ({selectedProductGroup.items.length})</p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Serial No.</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Purchase Price</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                        <TableHead>Supplier</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedProductGroup.items.map((inv, index) => (
                        <React.Fragment key={index}>
                          <TableRow>
                            <TableCell>{inv.serial_number || `Entry ${index + 1}`}</TableCell>
                            <TableCell className="text-right">{inv.qty_in_stock}</TableCell>
                            <TableCell className="text-right">₹{inv.purchase_price?.toLocaleString('en-IN')}</TableCell>
                            <TableCell className="text-right font-medium">₹{inv.stock_value?.toLocaleString('en-IN')}</TableCell>
                            <TableCell>{inv.supplier_name || '-'}</TableCell>
                          </TableRow>
                          {inv.serial_numbers && inv.serial_numbers.length > 0 && (
                            <TableRow className="bg-amber-50">
                              <TableCell colSpan={5} className="py-2">
                                <div className="flex flex-wrap gap-1 items-center">
                                  <span className="text-xs font-medium text-amber-800 mr-1">S/N:</span>
                                  {inv.serial_numbers.map((sn, snIdx) => {
                                    const snVal = typeof sn === 'object' ? sn.serial_number : sn;
                                    const isSold = typeof sn === 'object' && (sn.sold || sn.status === 'sold');
                                    return (
                                      <Badge key={snIdx} variant="outline" className={`font-mono text-xs ${isSold ? 'bg-red-50 text-red-700 line-through' : 'bg-white text-amber-900'}`}>
                                        {snVal}{isSold ? ' (Sold)' : ''}
                                      </Badge>
                                    );
                                  })}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          ) : selectedInventory && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Product</Label>
                  <p className="font-medium">
                    {selectedInventory.product?.name}
                    {selectedInventory.product?.model && ` (${selectedInventory.product.model})`}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Brand</Label>
                  <p className="font-medium">{selectedInventory.product?.brand_name || '-'}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
                <div>
                  <Label className="text-gray-500">Total Quantity</Label>
                  <p className="text-2xl font-bold">{selectedInventory.total_qty}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Total Value</Label>
                  <p className="text-2xl font-bold text-green-700">₹{selectedInventory.total_value?.toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Batches</Label>
                  <p className="text-2xl font-bold">{selectedInventory.inventory?.length || 0}</p>
                </div>
              </div>

              {/* Batch-wise breakdown */}
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Serial No.</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Purchase Price</TableHead>
                      <TableHead className="text-right">Value</TableHead>
                      <TableHead>Last Purchase</TableHead>
                      <TableHead>Supplier</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedInventory.inventory?.map((inv, index) => (
                      <TableRow key={index}>
                        <TableCell>{inv.serial_number || 'No Serial'}</TableCell>
                        <TableCell className="text-right">{inv.qty_in_stock}</TableCell>
                        <TableCell className="text-right">₹{inv.purchase_price?.toLocaleString('en-IN')}</TableCell>
                        <TableCell className="text-right font-medium">₹{inv.stock_value?.toLocaleString('en-IN')}</TableCell>
                        <TableCell>{inv.last_purchase_date ? new Date(inv.last_purchase_date).toLocaleDateString('en-IN') : '-'}</TableCell>
                        <TableCell>{inv.supplier_name || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setViewDialogOpen(false); setSelectedProductGroup(null); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust Inventory Dialog */}
      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Inventory</DialogTitle>
          </DialogHeader>
          {selectedInventory && (
            <div className="space-y-4">
              <div>
                <Label className="text-gray-500">Product</Label>
                <p className="font-medium">{selectedInventory.product_name}</p>
              </div>
              <div>
                <Label className="text-gray-500">Current Quantity</Label>
                <p className="text-xl font-bold">{selectedInventory.qty_in_stock} {selectedInventory.unit}</p>
              </div>
              <div>
                <Label>Adjustment (+/-)</Label>
                <Input
                  type="number"
                  value={adjustData.qty_adjustment}
                  onChange={(e) => setAdjustData({ ...adjustData, qty_adjustment: parseFloat(e.target.value) || 0 })}
                  placeholder="Enter + to add or - to reduce"
                />
                <p className="text-sm text-gray-500 mt-1">
                  New Quantity: {selectedInventory.qty_in_stock + (adjustData.qty_adjustment || 0)}
                </p>
              </div>
              <div>
                <Label>Reason *</Label>
                <Input
                  value={adjustData.reason}
                  onChange={(e) => setAdjustData({ ...adjustData, reason: e.target.value })}
                  placeholder="Reason for adjustment"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAdjust}>Save Adjustment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ExportPasswordDialog {...protectedExport} />
    </div>
  );
};

export default InventoryList;
